import { UseFormReturnType, isNotEmpty, useForm } from '@mantine/form'
import { modals } from '@mantine/modals'
import showModalNotification from '@v2/commons/modals/ModalNotification/showModalNotification'
import { handleMutationError } from '@v2/commons/utils/errorHandling/handleMutationError'

import { LocationDto } from 'src/api/generated'
import { STATES } from 'src/common/config'

import { useUpdateLocationMutation } from '../api/location.api'
import { UpdateLocationRequest } from '../location.types'

export interface IUpdateClinicHook {
  form: UseFormReturnType<UpdateLocationRequest>
  onSubmit: () => void
  isLoading: boolean
  ALL_STATES: { label: string; value: string }[]
}

export interface UpdateClinicHookProps {
  clinicId: string
  clinicName: string
  location: LocationDto
}

export const useUpdateLocation = ({
  clinicId,
  clinicName,
  location,
}: UpdateClinicHookProps): IUpdateClinicHook => {
  const [updateLocation, { isLoading }] = useUpdateLocationMutation()

  const form = useForm<UpdateLocationRequest>({
    mode: 'uncontrolled',
    initialValues: {
      id: location.id,
      clinicName: clinicName,
      name: location.name,
      email: location.email,
      businessDays: location.businessDays,
      phoneNumber: location.phoneNumber,
      languages: location.languages,
      address: {
        streetAddress: location.address.streetAddress,
        city: location.address.city,
        state: location.address.state,
        zipCode: location.address.zipCode,
        parkingSpace: location.address.parkingSpace,
      },
      clinicId,
    },
    validateInputOnBlur: true,
    validate: {
      clinicId: isNotEmpty('Clinic Name is required'),
      name: isNotEmpty('Location Name is required'),
      businessDays: isNotEmpty('Location Operating Days & Hous is required'),
      phoneNumber: isNotEmpty('phoneNumber is required'),
      languages: isNotEmpty('Languages Spoken is required'),
      address: {
        streetAddress: isNotEmpty('Street Address is required'),
        city: isNotEmpty('City is required'),
        state: isNotEmpty('State is required'),
        zipCode: isNotEmpty('Zip Code is required'),
      },
    },
  })

  const onSubmit = () => {
    form.onSubmit(async (values) => {
      try {
        const newValues = { ...values }

        await updateLocation({ ...newValues }).unwrap()
        modals.closeAll()
        showModalNotification({
          title: 'Success',
          subtitle: 'Location successfully updated!',
        })
        return true
      } catch (err) {
        return handleMutationError(err, 'Updated location')
      }
    })()
  }

  const ALL_STATES = Object.values(STATES).map(({ value, match }) => {
    return {
      label: value,
      value: match[0],
    }
  })

  return {
    form,
    onSubmit,
    isLoading,
    ALL_STATES,
  }
}
